/**
 * Partial coverage: apply Mapbox traffic only where matched geometry follows
 * the source (Valhalla/Mapbox) route; fall back to baseline elsewhere.
 */

import { haversineMeters } from './densifyRoutePoints'
import { pointToPolylineDistanceM } from './matchingFactor'
import {
  colorForCongestion,
  colorForManeuverTraffic,
  worstCongestionLevel,
  classifyPredictedCongestionLevel,
  CONGESTION_COLORS,
} from './compareTraffic'
import { buildTimeDelta, mpsToMph, maxspeedToMph } from './formatters'

export const CORRIDOR_THRESHOLD_M = 25
/** Below this fraction of length, treat the whole leg as traffic-unavailable. */
export const MIN_LEG_COVERAGE = 0.15
/** Ignore uncovered gaps shorter than this (meters) when merging runs. */
export const MIN_GAP_M = 40

/**
 * Cumulative distances along geometry vertices (meters).
 * @param {Array<{ lon: number, lat: number }>} geometry
 * @returns {number[]}
 */
export function cumulativeDistancesM(geometry) {
  const cum = [0]
  for (let i = 0; i < geometry.length - 1; i++) {
    cum.push(cum[i] + haversineMeters(geometry[i], geometry[i + 1]))
  }
  return cum
}

/**
 * Project point onto polyline; return distance along line and lateral distance.
 * @returns {{ distAlongM: number, distToLineM: number, segIndex: number }}
 */
export function projectOntoPolyline(point, lineCoordsLonLat) {
  if (!lineCoordsLonLat || lineCoordsLonLat.length < 2) {
    return { distAlongM: 0, distToLineM: Infinity, segIndex: 0 }
  }
  let best = {
    distAlongM: 0,
    distToLineM: Infinity,
    segIndex: 0,
  }
  let alongBefore = 0
  for (let i = 0; i < lineCoordsLonLat.length - 1; i++) {
    const start = {
      lon: lineCoordsLonLat[i][0],
      lat: lineCoordsLonLat[i][1],
    }
    const end = {
      lon: lineCoordsLonLat[i + 1][0],
      lat: lineCoordsLonLat[i + 1][1],
    }
    const segLen = haversineMeters(start, end)
    const { distM, ratio } = pointToSegmentProjection(point, start, end)
    if (distM < best.distToLineM) {
      best = {
        distAlongM: alongBefore + ratio * segLen,
        distToLineM: distM,
        segIndex: i,
      }
    }
    alongBefore += segLen
  }
  return best
}

function pointToSegmentProjection(point, start, end) {
  const lat0 = ((start.lat + end.lat + point.lat) / 3) * (Math.PI / 180)
  const cos = Math.cos(lat0)
  const ax = start.lon * cos
  const ay = start.lat
  const bx = end.lon * cos
  const by = end.lat
  const px = point.lon * cos
  const py = point.lat
  const dx = bx - ax
  const dy = by - ay
  const len2 = dx * dx + dy * dy
  let ratio = 0
  if (len2 > 0) {
    ratio = ((px - ax) * dx + (py - ay) * dy) / len2
    ratio = Math.max(0, Math.min(1, ratio))
  }
  const cx = ax + ratio * dx
  const cy = ay + ratio * dy
  const closest = { lon: cos !== 0 ? cx / cos : start.lon, lat: cy }
  return { distM: haversineMeters(point, closest), ratio }
}

/**
 * Per-vertex coverage vs matched Mapbox line.
 */
export function buildCoverageMask(
  sourceGeometry,
  mapboxLineCoords,
  thresholdM
) {
  const limit = thresholdM ?? CORRIDOR_THRESHOLD_M
  const covered = []
  for (const point of sourceGeometry) {
    const dist = pointToPolylineDistanceM(point, mapboxLineCoords)
    covered.push(dist <= limit)
  }
  return covered
}

/**
 * Merge tiny uncovered gaps between covered vertices.
 */
export function smoothCoverageMask(
  covered,
  sourceCumDist,
  minGapM = MIN_GAP_M
) {
  if (!covered.length) {
    return covered
  }
  const out = covered.slice()
  let i = 0
  while (i < out.length) {
    if (out[i]) {
      i += 1
      continue
    }
    let j = i
    while (j < out.length && !out[j]) {
      j += 1
    }
    const gapStart = i > 0 ? sourceCumDist[i] : 0
    const gapEnd = sourceCumDist[Math.min(j, sourceCumDist.length - 1)]
    const gapM = gapEnd - gapStart
    const bordered = i > 0 && j < out.length && out[i - 1] && out[j]
    if (bordered && gapM < minGapM) {
      for (let idx = i; idx < j; idx++) {
        out[idx] = true
      }
    }
    i = j
  }
  return out
}

/**
 * Sum annotation slices whose cumulative distance overlaps [fromM, toM].
 */
export function sumAnnotationsInRange(annotations, fromM, toM) {
  const distances = annotations?.distance || []
  const durations = annotations?.duration || []
  const congestions = annotations?.congestion || []
  const speeds = annotations?.speed || []
  const maxspeeds = annotations?.maxspeed || []

  let trafficTime = 0
  let trafficDistanceM = 0
  const congestionCounts = {}
  let speedSum = 0
  let speedN = 0
  const maxspeedSamples = []
  let along = 0

  const rangeStart = Math.min(fromM, toM)
  const rangeEnd = Math.max(fromM, toM)

  for (let i = 0; i < distances.length; i++) {
    const segDist = distances[i] || 0
    const segStart = along
    const segEnd = along + segDist
    along = segEnd
    const overlap = Math.min(segEnd, rangeEnd) - Math.max(segStart, rangeStart)
    if (overlap <= 0) {
      continue
    }
    const frac = segDist > 0 ? overlap / segDist : 0
    if (typeof durations[i] === 'number') {
      trafficTime += durations[i] * frac
    }
    trafficDistanceM += overlap
    const level = congestions[i]
    if (level) {
      congestionCounts[level] = (congestionCounts[level] || 0) + 1
    }
    if (typeof speeds[i] === 'number') {
      speedSum += speeds[i]
      speedN += 1
    }
    if (maxspeeds[i] != null) {
      maxspeedSamples.push(maxspeeds[i])
    }
  }

  return {
    trafficTime,
    trafficDistanceM,
    congestionCounts,
    avgSpeedMps: speedN ? speedSum / speedN : null,
    maxspeedSamples,
  }
}

function baselineTimeForSpan(
  maneuvers,
  beginIdx,
  endIdx,
  legDurationSec,
  spanLengthM,
  legLengthM
) {
  let time = 0
  let found = false
  for (const mnv of maneuvers || []) {
    if (mnv.end_shape_index <= beginIdx || mnv.begin_shape_index >= endIdx) {
      continue
    }
    if (typeof mnv.time === 'number') {
      // Proportion of maneuver overlapping [beginIdx, endIdx] by shape index count
      const mStart = mnv.begin_shape_index
      const mEnd = mnv.end_shape_index
      const overlap = Math.min(mEnd, endIdx) - Math.max(mStart, beginIdx)
      const mSpan = Math.max(1, mEnd - mStart)
      time += mnv.time * (overlap / mSpan)
      found = true
    }
  }
  if (found) {
    return time
  }
  if (legDurationSec != null && legLengthM > 0 && spanLengthM > 0) {
    return legDurationSec * (spanLengthM / legLengthM)
  }
  return 0
}

/**
 * Apply partial coverage and build hybrid leg traffic result.
 *
 * @param {object} params
 * @param {object} params.normalizedLeg
 * @param {GeoJSON.LineString} params.mapboxGeometry
 * @param {object} params.annotations
 * @param {number} [params.mapboxDuration]
 * @param {number} [params.mapboxDistanceM]
 * @param {number} [params.matchingFactor]
 * @param {object} [params.matchingDetails]
 * @param {'live'|'predicted'} [params.mapColorMode]
 *   `live` = Mapbox congestion annotations (near-now).
 *   `predicted` = duration vs free-flow (future depart / arrive-by).
 */
export function applyPartialCoverage({
  normalizedLeg,
  mapboxGeometry,
  annotations,
  mapboxDuration = null,
  mapboxDistanceM = null,
  matchingFactor = null,
  matchingDetails = null,
  mapColorMode = 'live',
}) {
  const geometry = normalizedLeg.geometry || []
  const mapboxCoords = mapboxGeometry?.coordinates || []
  const maneuvers = normalizedLeg.maneuvers || []
  const legDurationSec = normalizedLeg.durationSec
  const legDistanceM = normalizedLeg.distanceM || 0

  if (geometry.length < 2 || mapboxCoords.length < 2) {
    return unavailableLegResult(normalizedLeg, {
      reason: 'missing_geometry',
      matchingFactor,
      matchingDetails,
      mapboxDuration,
      mapboxDistanceM,
    })
  }

  const sourceCum = cumulativeDistancesM(geometry)
  const totalSourceM = sourceCum[sourceCum.length - 1] || legDistanceM || 0

  let covered = buildCoverageMask(geometry, mapboxCoords)
  covered = smoothCoverageMask(covered, sourceCum)

  let coveredLengthM = 0
  for (let i = 0; i < geometry.length - 1; i++) {
    if (covered[i] && covered[i + 1]) {
      coveredLengthM += sourceCum[i + 1] - sourceCum[i]
    }
  }
  const coveragePct =
    totalSourceM > 0 ? (coveredLengthM / totalSourceM) * 100 : 0

  if (coveragePct / 100 < MIN_LEG_COVERAGE) {
    return unavailableLegResult(normalizedLeg, {
      reason: 'low_coverage',
      coveragePct,
      matchingFactor,
      matchingDetails,
      mapboxDuration,
      mapboxDistanceM,
      coveredLengthM,
    })
  }

  // Build edge contributions
  let matchedTime = 0
  let fallbackTime = 0
  const edgeMeta = [] // per source edge

  for (let i = 0; i < geometry.length - 1; i++) {
    const edgeLen = sourceCum[i + 1] - sourceCum[i]
    const isCovered = covered[i] && covered[i + 1]
    if (isCovered) {
      const projA = projectOntoPolyline(geometry[i], mapboxCoords)
      const projB = projectOntoPolyline(geometry[i + 1], mapboxCoords)
      const summed = sumAnnotationsInRange(
        annotations,
        projA.distAlongM,
        projB.distAlongM
      )
      // If annotation sum is empty (projection quirk), fall back to length share of mapbox duration
      let edgeTime = summed.trafficTime
      if (!(edgeTime > 0) && mapboxDuration != null && mapboxDistanceM > 0) {
        edgeTime = mapboxDuration * (edgeLen / mapboxDistanceM)
      }
      matchedTime += edgeTime
      edgeMeta.push({
        begin: i,
        end: i + 1,
        covered: true,
        time: edgeTime,
        lengthM: edgeLen,
        congestionCounts: summed.congestionCounts,
        avgSpeedMps: summed.avgSpeedMps,
        maxspeedSamples: summed.maxspeedSamples,
      })
    } else {
      const edgeTime = baselineTimeForSpan(
        maneuvers,
        i,
        i + 1,
        legDurationSec,
        edgeLen,
        totalSourceM
      )
      fallbackTime += edgeTime
      edgeMeta.push({
        begin: i,
        end: i + 1,
        covered: false,
        time: edgeTime,
        lengthM: edgeLen,
        congestionCounts: {},
        avgSpeedMps: null,
        maxspeedSamples: [],
      })
    }
  }

  const hybridTime = matchedTime + fallbackTime

  // Maneuver rollup
  const maneuverResults = maneuvers.map((mnv) => {
    const begin = mnv.begin_shape_index
    const end = mnv.end_shape_index
    let trafficTime = 0
    let trafficDistanceM = 0
    let matchedM = 0
    const congestionCounts = {}
    let coveredEdges = 0
    let totalEdges = 0

    for (const edge of edgeMeta) {
      if (edge.end <= begin || edge.begin >= end) {
        continue
      }
      totalEdges += 1
      trafficTime += edge.time
      trafficDistanceM += edge.lengthM
      if (edge.covered) {
        coveredEdges += 1
        matchedM += edge.lengthM
        for (const [level, count] of Object.entries(
          edge.congestionCounts || {}
        )) {
          congestionCounts[level] = (congestionCounts[level] || 0) + count
        }
      }
    }

    const coverage =
      trafficDistanceM > 0 ? (matchedM / trafficDistanceM) * 100 : 0
    const usedTraffic = coveredEdges > 0 && coverage >= 50
    const displayTime = usedTraffic ? trafficTime : mnv.time ?? trafficTime
    const delta = buildTimeDelta(mnv.time, displayTime)

    return {
      instruction: mnv.instruction,
      begin_shape_index: begin,
      end_shape_index: end,
      valhalla: {
        time: mnv.time ?? null,
        length: mnv.length ?? null,
        lengthM: mnv.lengthM ?? null,
      },
      traffic: {
        time: displayTime,
        lengthM: trafficDistanceM || mnv.lengthM || null,
        lengthMiles: trafficDistanceM > 0 ? trafficDistanceM / 1609.344 : null,
        congestionCounts,
        matchedTime: usedTraffic ? trafficTime : null,
        coveragePct: Math.round(coverage),
        partial: coveredEdges > 0 && coveredEdges < totalEdges,
        unavailable: coveredEdges === 0,
      },
      delta,
      color: usedTraffic
        ? colorForManeuverTraffic(congestionCounts, mnv.time, displayTime)
        : 'unavailable',
    }
  })

  // Future / arrive-by: keep ETA from Mapbox durations, but do not paint
  // predicted congestion on the map (too unreliable for now).
  const congestionSegments =
    mapColorMode === 'predicted'
      ? []
      : buildHybridMapSegments({
          geometry,
          covered,
          mapboxCoords,
          annotations,
          mapColorMode,
        })

  return {
    ok: true,
    trafficUnavailable: false,
    coveragePct: Math.round(coveragePct * 10) / 10,
    coveredLengthM,
    totalLengthM: totalSourceM,
    matchedTime,
    fallbackTime,
    hybridTime,
    baselineTime: legDurationSec,
    baselineDistanceM: legDistanceM || totalSourceM,
    // Distance stays baseline (exact)
    hybridDistanceM: legDistanceM || totalSourceM,
    mapboxDuration,
    mapboxDistanceM,
    matchingFactor,
    matchingDetails,
    mapColorMode,
    maneuvers: maneuverResults,
    congestionSegments,
    annotations,
    debug: {
      coveragePct: Math.round(coveragePct * 10) / 10,
      coveredLengthM: Math.round(coveredLengthM),
      totalLengthM: Math.round(totalSourceM),
      matchedTime: Math.round(matchedTime),
      fallbackTime: Math.round(fallbackTime),
      hybridTime: Math.round(hybridTime),
      mapboxDuration:
        mapboxDuration != null ? Math.round(mapboxDuration) : null,
      mapboxDistanceM:
        mapboxDistanceM != null ? Math.round(mapboxDistanceM) : null,
      baselineTime: legDurationSec != null ? Math.round(legDurationSec) : null,
      annotationCount: annotations?.duration?.length ?? 0,
      sourceVertexCount: geometry.length,
      matchingFactor,
      matchingDetails,
    },
  }
}

function unavailableLegResult(normalizedLeg, extra = {}) {
  const legDurationSec = normalizedLeg.durationSec
  const legDistanceM = normalizedLeg.distanceM || 0
  const geometry = normalizedLeg.geometry || []
  const unavailableSegments =
    geometry.length >= 2
      ? [
          {
            coordinates: geometry.map((point) => [point.lon, point.lat]),
            color: CONGESTION_COLORS.unavailable,
            congestion: 'unavailable',
            avgSpeedMps: null,
            avgSpeedMph: null,
            maxspeed: null,
            maxspeedMph: null,
          },
        ]
      : []

  const maneuvers = (normalizedLeg.maneuvers || []).map((mnv) => ({
    instruction: mnv.instruction,
    begin_shape_index: mnv.begin_shape_index,
    end_shape_index: mnv.end_shape_index,
    valhalla: {
      time: mnv.time ?? null,
      length: mnv.length ?? null,
      lengthM: mnv.lengthM ?? null,
    },
    traffic: {
      time: mnv.time ?? null,
      lengthM: mnv.lengthM ?? null,
      lengthMiles: mnv.lengthM != null ? mnv.lengthM / 1609.344 : null,
      congestionCounts: {},
      matchedTime: null,
      coveragePct: 0,
      partial: false,
      unavailable: true,
    },
    delta: buildTimeDelta(mnv.time, mnv.time),
    color: 'unavailable',
  }))

  return {
    ok: true,
    trafficUnavailable: true,
    coveragePct: extra.coveragePct ?? 0,
    coveredLengthM: extra.coveredLengthM ?? 0,
    totalLengthM: legDistanceM,
    matchedTime: 0,
    fallbackTime: legDurationSec || 0,
    hybridTime: legDurationSec || 0,
    baselineTime: legDurationSec,
    baselineDistanceM: legDistanceM,
    hybridDistanceM: legDistanceM,
    mapboxDuration: extra.mapboxDuration ?? null,
    mapboxDistanceM: extra.mapboxDistanceM ?? null,
    matchingFactor: extra.matchingFactor ?? null,
    matchingDetails: extra.matchingDetails ?? null,
    maneuvers,
    congestionSegments: unavailableSegments,
    annotations: null,
    debug: {
      reason: extra.reason || 'unavailable',
      coveragePct: extra.coveragePct ?? 0,
      matchingFactor: extra.matchingFactor,
      matchingDetails: extra.matchingDetails,
      mapboxDuration: extra.mapboxDuration,
      mapboxDistanceM: extra.mapboxDistanceM,
      baselineTime: legDurationSec,
    },
  }
}

/**
 * Map segments on the **source route geometry** (aligned with maneuvers).
 * Near-now: Mapbox `congestion` annotations.
 * Future / arrive-by: predicted delay from duration vs maxspeed free-flow.
 */
function buildHybridMapSegments({
  geometry,
  covered,
  mapboxCoords,
  annotations,
  mapColorMode = 'live',
}) {
  const usePredicted = mapColorMode === 'predicted'
  const congestions = annotations?.congestion || []
  const distances = annotations?.distance || []
  const durations = annotations?.duration || []
  const speeds = annotations?.speed || []
  const maxspeeds = annotations?.maxspeed || []

  // Prefetch Mapbox cumulative distances for annotation lookup
  const mbCum = [0]
  for (let i = 0; i < distances.length; i++) {
    mbCum.push(mbCum[i] + (distances[i] || 0))
  }

  const annotationLevelsInRange = (fromM, toM) => {
    const rangeStart = Math.min(fromM, toM)
    const rangeEnd = Math.max(fromM, toM)
    const levels = []
    let speedSum = 0
    let speedN = 0
    let maxspeed = null
    let distSum = 0
    let durSum = 0
    for (let i = 0; i < distances.length; i++) {
      const segStart = mbCum[i]
      const segEnd = mbCum[i + 1]
      if (Math.min(segEnd, rangeEnd) - Math.max(segStart, rangeStart) <= 0) {
        continue
      }
      if (usePredicted) {
        levels.push(
          classifyPredictedCongestionLevel({
            distanceM: distances[i] || 0,
            durationSec: durations[i] || 0,
            maxspeed: maxspeeds[i],
          })
        )
      } else {
        levels.push(congestions[i] || 'unknown')
      }
      distSum += distances[i] || 0
      durSum += durations[i] || 0
      if (typeof speeds[i] === 'number') {
        speedSum += speeds[i]
        speedN += 1
      }
      if (maxspeeds[i] != null && maxspeed == null) {
        maxspeed = maxspeeds[i]
      }
    }
    // Fallback: nearest annotation by midpoint if range missed (projection quirks)
    if (levels.length === 0 && distances.length > 0) {
      const mid = (rangeStart + rangeEnd) / 2
      let idx = 0
      for (let i = 0; i < distances.length; i++) {
        if (mid >= mbCum[i] && mid <= mbCum[i + 1]) {
          idx = i
          break
        }
        if (mid > mbCum[i + 1]) {
          idx = i
        }
      }
      if (usePredicted) {
        levels.push(
          classifyPredictedCongestionLevel({
            distanceM: distances[idx] || 0,
            durationSec: durations[idx] || 0,
            maxspeed: maxspeeds[idx],
          })
        )
      } else {
        levels.push(congestions[idx] || 'unknown')
      }
      distSum += distances[idx] || 0
      durSum += durations[idx] || 0
      if (typeof speeds[idx] === 'number') {
        speedSum += speeds[idx]
        speedN += 1
      }
      if (maxspeeds[idx] != null) {
        maxspeed = maxspeeds[idx]
      }
    }
    let level = worstCongestionLevel(levels)
    // If predicted samples were all unknown, classify the aggregated span once
    if (usePredicted && level === 'unknown' && distSum > 0 && durSum > 0) {
      level = classifyPredictedCongestionLevel({
        distanceM: distSum,
        durationSec: durSum,
        maxspeed,
      })
    }
    // Predicted mode: prefer speed from duration/distance when Mapbox speed looks live
    let avgSpeedMps = speedN ? speedSum / speedN : null
    if (usePredicted && distSum > 0 && durSum > 0) {
      avgSpeedMps = distSum / durSum
    }
    return {
      level,
      avgSpeedMps,
      maxspeed,
    }
  }

  const rawSegments = []
  for (let i = 0; i < geometry.length - 1; i++) {
    const coords = [
      [geometry[i].lon, geometry[i].lat],
      [geometry[i + 1].lon, geometry[i + 1].lat],
    ]
    const isCovered = covered[i] && covered[i + 1]
    if (!isCovered) {
      rawSegments.push({
        coordinates: coords,
        color: CONGESTION_COLORS.unavailable,
        congestion: 'unavailable',
        colorMode: mapColorMode,
        avgSpeedMps: null,
        avgSpeedMph: null,
        maxspeed: null,
        maxspeedMph: null,
      })
      continue
    }

    const projA = projectOntoPolyline(geometry[i], mapboxCoords)
    const projB = projectOntoPolyline(geometry[i + 1], mapboxCoords)
    const sample = annotationLevelsInRange(projA.distAlongM, projB.distAlongM)
    rawSegments.push({
      coordinates: coords,
      color: colorForCongestion(sample.level),
      congestion: sample.level,
      colorMode: mapColorMode,
      avgSpeedMps: sample.avgSpeedMps,
      avgSpeedMph: mpsToMph(sample.avgSpeedMps),
      maxspeed: sample.maxspeed,
      maxspeedMph: maxspeedToMph(sample.maxspeed),
    })
  }

  // Also emit Mapbox-geometry segments (1:1 with annotations) so the overlay
  // matches Mapbox traffic flow even if source projection is sparse.
  const mapboxAnnotationSegments = buildMapboxAnnotationSegments(
    mapboxCoords,
    annotations,
    mapColorMode
  )

  // Color the source route (same geometry as maneuvers). Prefer Mapbox
  // annotation path only when source coverage is empty.
  if (rawSegments.some((seg) => seg.congestion !== 'unavailable')) {
    return mergeMapSegments(rawSegments)
  }
  if (mapboxAnnotationSegments.length > 0) {
    return mergeMapSegments(mapboxAnnotationSegments)
  }
  return mergeMapSegments(rawSegments)
}

/**
 * 1:1 Mapbox overview geometry ↔ annotations (congestion or predicted delay).
 */
function buildMapboxAnnotationSegments(
  mapboxCoords,
  annotations,
  mapColorMode = 'live'
) {
  const usePredicted = mapColorMode === 'predicted'
  const congestions = annotations?.congestion || []
  const distances = annotations?.distance || []
  const durations = annotations?.duration || []
  const speeds = annotations?.speed || []
  const maxspeeds = annotations?.maxspeed || []
  if (!Array.isArray(mapboxCoords) || mapboxCoords.length < 2) {
    return []
  }

  const segmentCount = mapboxCoords.length - 1
  const raw = []
  for (let i = 0; i < segmentCount; i++) {
    let level = 'unknown'
    const dist =
      distances.length === segmentCount ? distances[i] || 0 : distances[i] || 0
    const dur =
      durations.length === segmentCount ? durations[i] || 0 : durations[i] || 0
    const maxspeed =
      maxspeeds.length === segmentCount ? maxspeeds[i] : maxspeeds[i] || null

    if (usePredicted) {
      level = classifyPredictedCongestionLevel({
        distanceM: dist,
        durationSec: dur,
        maxspeed,
      })
    } else if (congestions.length === segmentCount) {
      level = congestions[i] || 'unknown'
    } else if (congestions.length > 0) {
      const idx = Math.min(
        congestions.length - 1,
        Math.floor((i / segmentCount) * congestions.length)
      )
      level = congestions[idx] || 'unknown'
    }
    const normalized = level === 'high' ? 'heavy' : level
    let avgSpeedMps =
      speeds.length === segmentCount && typeof speeds[i] === 'number'
        ? speeds[i]
        : null
    if (usePredicted && dist > 0 && dur > 0) {
      avgSpeedMps = dist / dur
    }
    raw.push({
      coordinates: [mapboxCoords[i], mapboxCoords[i + 1]],
      color: colorForCongestion(normalized),
      congestion: normalized,
      colorMode: mapColorMode,
      avgSpeedMps,
      avgSpeedMph: mpsToMph(avgSpeedMps),
      maxspeed,
      maxspeedMph: maxspeedToMph(maxspeed),
    })
  }
  return raw
}

function mergeMapSegments(segments) {
  if (!segments.length) {
    return []
  }
  const finalize = (seg) => {
    const avgSpeedMps =
      seg._speedN > 0 ? seg._speedSum / seg._speedN : seg.avgSpeedMps
    const out = {
      coordinates: seg.coordinates,
      color: seg.color,
      congestion: seg.congestion,
      colorMode: seg.colorMode || 'live',
      avgSpeedMps,
      avgSpeedMph: mpsToMph(avgSpeedMps),
      maxspeed: seg.maxspeed,
      maxspeedMph: maxspeedToMph(seg.maxspeed),
    }
    return out
  }
  const merged = []
  let current = {
    coordinates: [...segments[0].coordinates],
    color: segments[0].color,
    congestion: segments[0].congestion,
    colorMode: segments[0].colorMode || 'live',
    avgSpeedMps: segments[0].avgSpeedMps,
    maxspeed: segments[0].maxspeed,
    _speedSum: segments[0].avgSpeedMps || 0,
    _speedN: segments[0].avgSpeedMps != null ? 1 : 0,
  }
  for (let i = 1; i < segments.length; i++) {
    const next = segments[i]
    if (
      next.congestion === current.congestion &&
      (next.colorMode || 'live') === (current.colorMode || 'live')
    ) {
      current.coordinates.push(...next.coordinates.slice(1))
      if (next.avgSpeedMps != null) {
        current._speedSum += next.avgSpeedMps
        current._speedN += 1
      }
      if (current.maxspeed == null && next.maxspeed != null) {
        current.maxspeed = next.maxspeed
      }
    } else {
      merged.push(finalize(current))
      current = {
        coordinates: [...next.coordinates],
        color: next.color,
        congestion: next.congestion,
        colorMode: next.colorMode || 'live',
        avgSpeedMps: next.avgSpeedMps,
        maxspeed: next.maxspeed,
        _speedSum: next.avgSpeedMps || 0,
        _speedN: next.avgSpeedMps != null ? 1 : 0,
      }
    }
  }
  merged.push(finalize(current))
  return merged
}
